import React, { Component } from "react";
import PropTypes from "prop-types";
import utils from "@utils";
import { InvoiceEstimatesService } from "@services";
import { JOB_ESTIMATE_APPROVAL_STATUS, DAYS_OF_WEEK } from "@constants";
import axios from "axios";
import { Loader } from "../../../components";

import "./JobEstimate.scss";

let cancel;
const getCancelToken = c => {
	cancel = c;
};

class JobEstimate extends Component {
	constructor(props) {
		super(props);

		this.state = {
			jobEstimate: {},
			loading: false,
		};
	}

	componentDidMount() {
		const {
			router: {
				params: { estimateId },
			},
		} = this.props;
		this.getJobEstimate(estimateId);
	}

	componentDidUpdate(prevProps) {
		const {
			router: {
				params: { estimateId: prevId },
			},
		} = prevProps;
		const {
			router: {
				params: { estimateId },
			},
		} = this.props;
		// If the component has already mounted and the estimateId parameter changes without reloading, get new estimate details
		// Included here since notifications can route to this page
		if (prevId && estimateId && prevId !== estimateId) {
			this.getJobEstimate(estimateId);
		}
	}

	componentWillUnmount() {
		if (cancel) cancel();
	}

	/**
	 * Retrieves job estimate details from the API using the `hash` value provided
	 * @param {String} hash A token representing the job estimate to retrieve
	 */
	getJobEstimate = hash => {
		const { lang } = global;
		const { router } = this.props;

		this.setState({ loading: true });
		InvoiceEstimatesService.getInvoiceEstimateFromId(hash, getCancelToken)
			.then(({ data }) => {
				this.setState({ jobEstimate: data, loading: false });
				cancel = undefined;
			})
			.catch(error => {
				if (!axios.isCancel(error)) {
					this.setState({ loading: false });
					utils.notify({
						type: "error",
						message: lang.t("Error getting Job Estimate"),
					});
					router.push("/jobs/estimates");
				}
			});
	};

	/** Retrieves and downloads a PDF version of the job estimate */
	getInvoiceEstimatePDF = () => {
		const { lang } = global;
		const {
			router: {
				params: { estimateId },
			},
		} = this.props;

		this.setState({ loading: true });

		InvoiceEstimatesService.getInvoiceEstimatePDFFromId(estimateId)
			.then(({ data }) => {
				this.setState({ loading: false });
				utils.downloadReportFromBlobResponse(
					data,
					`rollkall_invoice_estimate_${estimateId}.pdf`,
				);
			})
			.catch(({ response }) => {
				this.setState({ loading: false });
				utils.notify({
					type: "error",
					message: response?.data?.Message ?? lang.t("Error getting Job Estimate"),
				});
			});
	};

	/** Navigates the user to the estimates list */
	onClickBack = () => {
		const {
			router: { push },
		} = this.props;
		push("/jobs/estimates");
	};

	/** Navigates the user to the job event associated with the current estimate */
	onClickViewJob = jobId => () => {
		const {
			router: { push },
		} = this.props;
		push(`/jobs/${jobId}`);
	};

	/**
	 * Renders a label to visually indicate an estimate's current status
	 * @param {Number} statusId The ID number for an estimate's status
	 */
	renderStatusLabel = statusId => {
		const status =
			Object.values(JOB_ESTIMATE_APPROVAL_STATUS).find(({ value }) => value === statusId) ??
			JOB_ESTIMATE_APPROVAL_STATUS.DEFAULT;

		return (
			<span className={`estimate-status-badge label label-${status.color}`}>
				{status.label}
			</span>
		);
	};

	/**
	 * Renders a properly formatted string to indicate which days of the week the job would occur on
	 * @param {String} recurring An unformatted string of days when the job should occur
	 * @returns {String} A formatted string with all days of the week spelled out completely
	 */
	formatRecurringDays = (recurring = "") => {
		// Create an object in the form: {mon: Monday, tue: Tuesday, ...}
		const daysOfWeek = DAYS_OF_WEEK.reduce(
			(acc, item) => ({ ...acc, [item.shortName]: item.label }),
			{},
		);

		// Replace days short name with full name
		return recurring
			.split(",")
			.map(item => daysOfWeek[item])
			.join(", ");
	};

	render() {
		const { lang } = global;
		const { user } = this.props;
		const { jobEstimate, loading } = this.state;
		const { InvoiceEstimate = {} } = jobEstimate;

		const {
			AvgRate = 0,
			TotalOfficers = 0,
			JurisdictionFees = [],
			TotalOfficerPay = 0,
			TotalOrganizationFee = 0,
			TotalJurisdictionFee = 0,
			RKPayFee = 0,
			Tax = 0,
			Total = 0,
			WeeklyTotal = 0,
		} = InvoiceEstimate;

		return (
			<div id="job-estimate-detail">
				<div
					id="estimate-detail-subnav"
					className="hidden-print"
				>
					<button
						type="button"
						className="btn btn-link"
						onClick={this.onClickBack}
						name="navigate-back"
						data-test-id="estimate__navigate-back"
					>
						<i className="rkf rkf-chevron-left" />
						<span>{lang.t("Job Estimates")}</span>
					</button>
					<button
						type="button"
						className="btn btn-primary"
						onClick={this.onClickViewJob(jobEstimate.JobEventId)}
						disabled={loading}
						data-test-id="estimate__view-job"
					>
						{lang.t("View Job")}
					</button>
				</div>

				<div
					id="estimate-detail-content"
					className="container"
				>
					<h1>{lang.t("Estimate Details")}</h1>
					<div
						className="row"
						id="estimate-details-body"
						data-test-id="estimate__details-body"
					>
						<div
							className="col-sm-8 col-xs-12"
							id="estimate-info"
							data-test-id="estimate__info"
						>
							<div
								className="row form-group"
								id="estimate-detail-name"
								data-test-id="estimate__detail-name"
							>
								<div className="col-xs-12 text-left">
									<If
										condition={
											jobEstimate.JobEventName && jobEstimate.JobEventId
										}
									>
										<h4>{`${jobEstimate.JobEventName} (${lang.t("Job")} #${jobEstimate.JobEventId})`}</h4>
									</If>
								</div>
							</div>
							<div className="row form-group">
								<div className="col-sm-3">
									<strong>{`${lang.t("Sent To")}`}</strong>
								</div>
								<div className="col-sm-9" data-test-id="estimate__client-contact-name">{jobEstimate.ClientContactName}</div>
							</div>
							<div className="row form-group">
								<div className="col-sm-3">
									<strong>{`${lang.t("Client")}`}</strong>
								</div>
								<div className="col-sm-9" data-test-id="estimate__client-name">{jobEstimate.ClientName}</div>
							</div>
							<div className="row form-group">
								<div className="col-sm-3" />
								<div className="col-sm-9">
									<div data-test-id="estimate__location-name">{jobEstimate.LocationName}</div>
									<div data-test-id="estimate__location-address">
										{`${jobEstimate?.LocationAddress?.StreetAddress ? `${jobEstimate.LocationAddress.StreetAddress}, ` : ""}
										 ${jobEstimate?.LocationAddress?.City ? `${jobEstimate.LocationAddress.City}, ` : ""}
										 ${jobEstimate?.LocationAddress?.StateName ? `${jobEstimate.LocationAddress.StateName}, ` : ""}
										 ${jobEstimate?.LocationAddress?.Zip ?? ""}`}
									</div>
								</div>
							</div>
							<div className="row form-group">
								<div className="col-sm-3" />
								<div className="col-sm-9" data-test-id="estimate__event-datetime">
									<span>
										{jobEstimate.JobEventStartDateTime &&
										jobEstimate.JobEventEndDateTime
											? `${utils.formatDate(`${jobEstimate.JobEventStartDateTime}Z`, "en-US", user.TimeZoneId, "short")},
										${utils.formatTime(`${jobEstimate.JobEventStartDateTime}Z`, user.UserPreferences !== "useMilitaryTime", user.TimeZoneId)} -
										${utils.formatTime(`${jobEstimate.JobEventEndDateTime}Z`, user.UserPreferences !== "useMilitaryTime", user.TimeZoneId)}`
											: ""}
									</span>
								</div>
							</div>
							<If condition={jobEstimate.JobEventRecurrenceRepeatsOn}>
								<div className="row form-group">
									<div className="col-sm-3">
										<strong>{`${lang.t("Recurring")}`}</strong>
									</div>
									<div className="col-sm-9" data-test-id="estimate__recurring-days">
										{this.formatRecurringDays(
											jobEstimate.JobEventRecurrenceRepeatsOn,
										)}
									</div>
								</div>
							</If>
							<div className="row form-group">
								<div className="col-sm-3" />
								<div className="col-sm-9">
									<button
										type="button"
										onClick={this.getInvoiceEstimatePDF}
										className="btn btn-link"
										data-test-id="estimate__download-pdf"
									>
										{lang.t("Download PDF")}
									</button>
								</div>
							</div>
						</div>

						<div
							className="col-sm-4 col-xs-12"
							id="estimate-summary"
						>
							<div className="row">
								<div
									className="col-xs-12"
									id="estimate-summary-header"
									data-test-id="estimate__summary-header"
								>
									<h4>{lang.t("Estimate")}</h4>
									<div>
										<If condition={jobEstimate.RequiresPrepayment}>
											<div
												className="label label-success"
												title={lang.t("Requires prepayment")}
											>
												{lang.t("Prepay")}
											</div>
										</If>
										{this.renderStatusLabel(jobEstimate.Status)}
									</div>
								</div>
							</div>
							<hr />
							<div className="form-group row">
								<div className="col-xs-7">
									<strong>{lang.t("Avg Officer Rate")}</strong>
								</div>
								<div className="col-xs-5 text-right" data-test-id="estimate__avg-officer-rate">{`${utils.currencyFormatter(AvgRate)}/hour`}</div>
							</div>
							<div className="form-group row">
								<div className="col-xs-7">
									<strong>{lang.t("Total Officers")}</strong>
								</div>
								<div className="col-xs-5 text-right" data-test-id="estimate__total-officers">{TotalOfficers}</div>
							</div>
							<For
								each="item"
								of={JurisdictionFees}
							>
								<div
									className="form-group row"
									key={`jurisdiction_fee_${item.ItemName}`}
								>
									<div className="col-xs-7 estimate-fee-name">
										<strong>{item.ItemName}</strong>
									</div>
									<div className="col-xs-5 text-right" data-test-id="estimate__jurisdiction-fee-subtotal">
										{utils.currencyFormatter(item.ItemSubtotal)}
									</div>
								</div>
							</For>
							<hr />

							<div className="form-group row">
								<div className="col-xs-7">
									<strong>{lang.t("Subtotal")}</strong>
								</div>
								<div className="col-xs-5 text-right" data-test-id="estimate__subtotal">
									{utils.currencyFormatter(TotalOfficerPay)}
								</div>
							</div>
							<div className="form-group row">
								<div className="col-xs-7">
									<strong>{lang.t("Coordination Fees")}</strong>
								</div>
								<div className="col-xs-5 text-right" data-test-id="estimate__coordination-fees">
									{utils.currencyFormatter(TotalOrganizationFee)}
								</div>
							</div>
							<div className="form-group row">
								<div className="col-xs-7">
									<strong>{lang.t("Jurisdiction Fees")}</strong>
								</div>
								<div className="col-xs-5 text-right" data-test-id="estimate__jurisdiction-fees">
									{utils.currencyFormatter(TotalJurisdictionFee)}
								</div>
							</div>
							<div className="form-group row">
								<div className="col-xs-7">
									<strong>{lang.t("RollKall Fees")}</strong>
								</div>
								<div className="col-xs-5 text-right" data-test-id="estimate__rollkall-fees">
									{utils.currencyFormatter(RKPayFee)}
								</div>
							</div>
							<div className="form-group row">
								<div className="col-xs-7">
									<strong>{lang.t("Taxes")}</strong>
								</div>
								<div className="col-xs-5 text-right" data-test-id="estimate__taxes">
									{utils.currencyFormatter(Tax)}
								</div>
							</div>
							<hr />
							<div className="form-group row">
								<div className="col-xs-7">
									<strong>
										{`${lang.t("Total Estimate")}${jobEstimate.JobEventRecurrenceRuleId ? ` (${lang.t("Daily")})` : ""}`}
									</strong>
								</div>
								<div className="col-xs-5 text-right" data-test-id="estimate__total">
									{utils.currencyFormatter(Total)}
								</div>
							</div>
							<If condition={jobEstimate.JobEventRecurrenceRuleId}>
								<div className="form-group row">
									<div className="col-xs-7">
										<strong>{`${lang.t("Total Estimate")} (${lang.t("Weekly")})`}</strong>
									</div>
									<div className="col-xs-5 text-right" data-test-id="estimate__weekly-total">
										{utils.currencyFormatter(WeeklyTotal)}
									</div>
								</div>
							</If>
						</div>
					</div>
					<Loader active={loading} />
				</div>
			</div>
		);
	}
}

JobEstimate.propTypes = {
	user: PropTypes.object,
	router: PropTypes.object.isRequired,
};

JobEstimate.defaultProps = {
	user: {},
};

export default JobEstimate;
